/**
 * Format a number with thousands separators
 * @param {number} num - The number to format
 * @param {number} [decimals=0] - Number of decimal places
 * @returns {string} Formatted number
 */
export const formatNumber = (num, decimals = 0) => {
  if (num === null || num === undefined) return "0"
  return Number(num).toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
}

/**
 * Format a number as currency
 * @param {number} num - The number to format
 * @param {string} [currency='USD'] - Currency code
 * @returns {string} Formatted currency
 */
export const formatCurrency = (num, currency = "USD") => {
  if (num === null || num === undefined) return "$0"
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
  }).format(num)
}

/**
 * Format a date
 * @param {string|Date} date - The date to format
 * @param {object} [options] - Formatting options
 * @returns {string} Formatted date
 */
export const formatDate = (date, options = {}) => {
  if (!date) return "N/A"
  const defaultOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
  }
  const mergedOptions = { ...defaultOptions, ...options }
  return new Date(date).toLocaleDateString(undefined, mergedOptions)
}

/**
 * Format a date and time
 * @param {string|Date} date - The date to format
 * @returns {string} Formatted date and time
 */
export const formatDateTime = (date) => {
  if (!date) return "N/A"
  return new Date(date).toLocaleString()
}

/**
 * Format a date as relative time (e.g., "2 hours ago")
 * @param {string|Date} date - The date to format
 * @returns {string} Relative time
 */
export const formatRelativeTime = (date) => {
  if (!date) return "N/A"

  const now = new Date()
  const past = new Date(date)
  const diffMs = now - past

  // Convert to seconds
  const diffSec = Math.floor(diffMs / 1000)

  if (diffSec < 60) return `${diffSec} second${diffSec !== 1 ? "s" : ""} ago`

  // Convert to minutes
  const diffMin = Math.floor(diffSec / 60)
  if (diffMin < 60) return `${diffMin} minute${diffMin !== 1 ? "s" : ""} ago`

  // Convert to hours
  const diffHour = Math.floor(diffMin / 60)
  if (diffHour < 24) return `${diffHour} hour${diffHour !== 1 ? "s" : ""} ago`

  // Convert to days
  const diffDay = Math.floor(diffHour / 24)
  if (diffDay < 30) return `${diffDay} day${diffDay !== 1 ? "s" : ""} ago`

  // Convert to months
  const diffMonth = Math.floor(diffDay / 30)
  if (diffMonth < 12) return `${diffMonth} month${diffMonth !== 1 ? "s" : ""} ago`

  // Convert to years
  const diffYear = Math.floor(diffMonth / 12)
  return `${diffYear} year${diffYear !== 1 ? "s" : ""} ago`
}

